import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { type RefObject, useEffect, useRef, useState } from "react";

import SegmentedTabs from "@/blueprint/components/SegmentedTabs/SegmentedTabs";
import { seconds } from "@/blueprint/tokens/Motion";
import { useTrack } from "@/analytics/events";
import { useOnScreen } from "@/hooks/useOnScreen";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { playVideo } from "@/lib/playVideo";
import { optimizedPoster } from "@/lib/optimizedPoster";
import { cn } from "@/lib/utils";
import type { MaticModelId } from "@/views/shop/shopContent";
import {
  FEATURE_TABS,
  type BenefitTab,
  type FeatureClip,
  type FeatureTabId,
  featureTabsFor,
} from "@/views/shop/featureTabsContent";

/*
 * The visual zone's feature tabs, after Tesla's Model Y design page: a tab
 * bar along the zone's foot, Overview for the film, Matic Design for the 3D
 * model, and a playlist of clips for every other tab (MaticGallery).
 */

// Tabs change often: a quick fade, so a pick never feels held up.
const FADE_MS = 300;

type FeatureTab = ReturnType<typeof featureTabsFor>[number];

const isBenefitTab = (tab: FeatureTab): tab is BenefitTab =>
  tab.id !== FEATURE_TABS.overview.id && tab.id !== FEATURE_TABS.design.id;

/** The model's benefit tab for an id; null for Overview and Matic Design. */
export const benefitTab = (
  modelId: MaticModelId,
  tabId: FeatureTabId,
): BenefitTab | null => {
  const tab = featureTab(modelId, tabId);
  return isBenefitTab(tab) ? tab : null;
};

/** A tab's clips that can play now; a still still waiting on its asset drops. */
export const playableClips = (tab: BenefitTab): readonly FeatureClip[] =>
  tab.clips.filter((clip) => clip.kind === "video" || clip.image !== null);

/** The model's tab for an id; Overview when the id isn't one of them. */
const featureTab = (modelId: MaticModelId, tabId: FeatureTabId): FeatureTab =>
  featureTabsFor(modelId).find(({ id }) => id === tabId) ??
  FEATURE_TABS.overview;

/**
 * The small dot the quiet tab carries for a couple of seconds after a model
 * switch, saying its content changed without taking the shopper there.
 */
const ChangedDot = ({ isVisible }: { isVisible: boolean }) => {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.span
          aria-hidden
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={
            reducedMotion ? { duration: 0 } : { duration: seconds(200) }
          }
          className="absolute right-[2px] top-[2px] size-[8px] rounded-full bg-brand-blue ring-2 ring-brand-white"
        />
      )}
    </AnimatePresence>
  );
};

/**
 * Tesla's tab bar, centred on the zone's foot from `tab`, drawn as the site's
 * pill switcher (SegmentedTabs on media: a dark pill, the active tab white).
 */
export const FeatureTabBar = ({
  modelId,
  activeId,
  quietChanged,
  onSelect,
  onIntent,
  size = "md",
  className = "",
}: {
  modelId: MaticModelId;
  activeId: FeatureTabId;
  quietChanged: boolean;
  onSelect: (id: FeatureTabId) => void;
  /** A pointer on a tab or focus on it, before it's picked. */
  onIntent: (id: FeatureTabId) => void;
  /** "sm" for the phone's row under the zone. */
  size?: "md" | "sm";
  className?: string;
}) => {
  const tabs = featureTabsFor(modelId);
  return (
    <SegmentedTabs
      usage="controls"
      tone="onMedia"
      size={size}
      scrollable
      fadeEdges
      tabs={tabs.map(({ id, label }) => ({ id, label }))}
      activeId={activeId}
      onChange={(id) => {
        const tab = tabs.find((item) => item.id === id);
        if (tab !== undefined) onSelect(tab.id);
      }}
      onTabIntent={(id) => {
        const tab = tabs.find((item) => item.id === id);
        if (tab !== undefined) onIntent(tab.id);
      }}
      ariaLabel={FEATURE_TABS.label}
      markerFor={(id) =>
        id === "quiet" ? <ChangedDot isVisible={quietChanged} /> : null
      }
      className={className}
    />
  );
};

/**
 * The same pill over a carousel's own slides, for a set whose slides are
 * categories (the add-ons): each tab shows its slide, and the arrows and
 * swipes keep it in step.
 */
export const SlideTabBar = <Id extends string>({
  label,
  slides,
  activeId,
  labelOf,
  onSelect,
  size = "md",
  className = "",
}: {
  label: string;
  slides: readonly Id[];
  activeId: Id;
  labelOf: (id: Id) => string;
  onSelect: (id: Id) => void;
  /** "sm" where the image column is narrow. */
  size?: "md" | "sm";
  className?: string;
}) => (
  <SegmentedTabs
    usage="controls"
    tone="onMedia"
    size={size}
    scrollable
    fadeEdges
    tabs={slides.map((id) => ({ id, label: labelOf(id) }))}
    activeId={activeId}
    onChange={(id) => {
      const slide = slides.find((item) => item === id);
      if (slide !== undefined) onSelect(slide);
    }}
    ariaLabel={label}
    className={className}
  />
);

/**
 * True while the element is at least partly on screen and the page is
 * showing: what a playing clip needs to keep playing.
 */
const useVisible = (ref: RefObject<HTMLElement | null>) => {
  const onScreen = useOnScreen(ref);
  const [pageShown, setPageShown] = useState(true);

  useEffect(() => {
    const update = () => setPageShown(document.visibilityState === "visible");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  return onScreen && pageShown;
};

/**
 * Plays the video while `playing` and pauses it otherwise; `clipKey` runs it
 * again for each new clip the element is handed.
 */
export const useVideoPlayback = (
  videoRef: RefObject<HTMLVideoElement | null>,
  playing: boolean,
  clipKey: string | undefined,
) => {
  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    if (playing) playVideo(video);
    else video.pause();
  }, [videoRef, playing, clipKey]);
};

/**
 * A benefit tab's playlist, covering the zone: muted inline clips one after
 * another, looping the list; a still holds for its time. Only the clip on
 * screen loads (metadata and poster), while the next one is fetched as it
 * plays. Playback pauses off screen and while the page is hidden; reduced
 * motion shows posters only and leaves the bullets to page. Half a clip
 * played reports `visual_clip_viewed`.
 */
export const FeaturePlayer = ({
  tab,
  modelId,
  clipIndex,
  onAdvance,
  sizes,
  paused,
  onTime,
}: {
  tab: BenefitTab;
  modelId: MaticModelId;
  /** Index into the tab's playable clips. */
  clipIndex: number;
  /** The clip ended or its still's time is up. */
  onAdvance: () => void;
  sizes: string;
  /** Held by the zone's pause button: the clip stops and nothing advances. */
  paused: boolean;
  /** The playing clip's playhead, in seconds, for a timed caption. */
  onTime: (seconds: number) => void;
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const track = useTrack();
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVisible = useVisible(rootRef);
  const clips = playableClips(tab);
  const clip = clips[clipIndex % Math.max(clips.length, 1)];
  const next = clips[(clipIndex + 1) % Math.max(clips.length, 1)];
  const reportedRef = useRef<string | null>(null);

  const sourceOf = (item: FeatureClip) =>
    item.kind === "video" ? item.video : null;

  // Play while seen and not paused; off screen and hidden, it pauses too.
  // Reduced motion starts the zone paused, so playing is the shopper's call.
  useVideoPlayback(videoRef, isVisible && !paused, clip?.id);

  // A still holds for its time, then hands on; never while paused.
  useEffect(() => {
    if (clip?.kind !== "image" || paused || !isVisible) return;
    const hold = window.setTimeout(onAdvance, clip.holdMs);
    return () => window.clearTimeout(hold);
  }, [clip, paused, isVisible, onAdvance]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video === null || clip === undefined) return;
    onTime(video.currentTime);
    const key = `${tab.id}:${clip.id}:${clipIndex}`;
    if (reportedRef.current === key) return;
    if (video.duration > 0 && video.currentTime >= video.duration / 2) {
      reportedRef.current = key;
      track("visual_clip_viewed", {
        tab_id: tab.id,
        clip_id: clip.id,
        model: modelId,
      });
    }
  };

  const nextSource = next === undefined ? null : sourceOf(next);

  return (
    <div ref={rootRef} className="absolute inset-0">
      <AnimatePresence initial={false}>
        {clip !== undefined && (
          <motion.div
            key={clip.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={featureFade(reducedMotion)}
            className="absolute inset-0"
          >
            {clip.kind === "video" ? (
              <video
                // Kept on the newest clip: the one fading out unmounts after
                // the next has mounted, and must not clear it.
                ref={(element) => {
                  if (element !== null) videoRef.current = element;
                }}
                src={sourceOf(clip) ?? undefined}
                poster={optimizedPoster(clip.poster.src, 1920)}
                preload="metadata"
                muted
                playsInline
                autoPlay={!paused}
                aria-label={clip.alt}
                // A one-clip list loops on its own.
                loop={clips.length === 1}
                onEnded={onAdvance}
                onTimeUpdate={handleTimeUpdate}
                className="pointer-events-none absolute inset-0 size-full object-cover"
              />
            ) : clip.image === null ? null : (
              <Image
                src={clip.image}
                alt={clip.alt}
                fill
                sizes={sizes}
                draggable={false}
                className="pointer-events-none select-none object-cover"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {/* The next clip, fetched while this one plays. */}
      {!reducedMotion &&
        isVisible &&
        nextSource !== null &&
        next?.id !== clip?.id && (
          <video
            aria-hidden
            src={nextSource}
            preload="auto"
            muted
            playsInline
            className="pointer-events-none invisible absolute size-px"
          />
        )}
    </div>
  );
};

/** The crossfade a tab's media and card take when they change. */
export const featureFade = (reducedMotion: boolean) =>
  reducedMotion
    ? { duration: 0 }
    : { duration: seconds(FADE_MS), ease: "easeOut" as const };

/**
 * A few clips played one after another, looping, covering their box: the
 * add-ons' videos in the visual zone and the details dialog. Muted and
 * inline; paused off screen and while the page is hidden; posters only
 * under reduced motion.
 */
export const ClipSequence = ({
  clips,
  paused,
  fit = "cover",
}: {
  clips: readonly FeatureClip[];
  /** Held by a pause button: the clip stops and the sequence waits. */
  paused: boolean;
  /** `contain` keeps a product shot whole in a box of another shape. */
  fit?: "cover" | "contain";
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isVisible = useVisible(rootRef);
  const [index, setIndex] = useState(0);
  const clip = clips[index % Math.max(clips.length, 1)];

  useVideoPlayback(videoRef, isVisible && !paused, clip?.id);

  if (clip === undefined || clip.kind !== "video") return null;

  return (
    <div ref={rootRef} className="absolute inset-0">
      <AnimatePresence initial={false}>
        <motion.div
          key={clip.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={featureFade(reducedMotion)}
          className="absolute inset-0"
        >
          <video
            // Kept on the newest clip, as in FeaturePlayer.
            ref={(element) => {
              if (element !== null) videoRef.current = element;
            }}
            src={clip.video}
            poster={optimizedPoster(clip.poster.src, 1920)}
            preload="metadata"
            muted
            playsInline
            autoPlay={!paused}
            loop={clips.length === 1}
            aria-label={clip.alt}
            onEnded={() => setIndex((current) => (current + 1) % clips.length)}
            className={cn(
              "pointer-events-none absolute inset-0 size-full",
              fit === "cover" ? "object-cover" : "object-contain",
            )}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
